/**
 * Integrytas Group - Common Security Module (CSM)
 * Pure data loader that hydrates an AuthSession from Supabase tables.
 *
 * Kept separate from the React hook so it can be tested in isolation
 * and reused by server-side code (RPCs, edge functions).
 */

import type { User } from '@supabase/supabase-js';
import { apiDb } from './api_client';
import { isNavigableApplication } from './page_scope';
import type {
  AuthSession,
  LocaleCode,
  PageAccess,
  RoleSummary,
  UserContext,
} from '../types/auth';

const DEFAULT_LOCALE: LocaleCode =
  (import.meta.env.VITE_DEFAULT_LOCALE as LocaleCode) || 'EN_US';

interface UserMasterRow {
  user_id: number;
  internal_code: string;
  email: string;
  display_name: string;
  preferred_locale: LocaleCode;
  is_admin: 'Y' | 'N';
  can_deactivate_users: 'Y' | 'N';
  can_mimic_users: 'Y' | 'N';
  manager_user_id: number | null;
  is_active: 'Y' | 'N';
}

interface RoleAssignmentRow {
  role_id: number;
  csm_role_master: { internal_code: string };
}

interface PageAccessRow {
  page_id: number;
  can_view: boolean;
  can_add: boolean;
  can_change: boolean;
  can_delete: boolean;
  can_signoff: boolean;
  can_personalize_role: boolean;
  can_personalize_user: boolean;
  csm_page_master: {
    internal_code: string;
    route_path: string;
    parent_page_id: number | null;
    sort_order: number;
    desktop_only: 'Y' | 'N';
    application_id: number | null;
  };
}

/**
 * Hydrates the localized labels for a list of role/page ids from the
 * matching *_tl table. Falls back to internal_code if no row exists.
 */
async function loadTranslations(
  table: 'csm_role_master_tl' | 'csm_page_master_tl',
  idColumn: 'role_id' | 'page_id',
  ids: number[],
  locale: LocaleCode,
): Promise<Map<number, string>> {
  const labels = new Map<number, string>();
  if (ids.length === 0) return labels;

  const { data, error } = await apiDb
    .from(table)
    .select(`${idColumn}, locale_code, display_text`)
    .in(idColumn, ids)
    .in('locale_code', [locale, DEFAULT_LOCALE]);

  if (error) throw error;

  // Prefer the user's locale; fall back to default.
  for (const row of (data ?? []) as Record<string, unknown>[]) {
    const id = row[idColumn] as number;
    const isPreferred = row.locale_code === locale;
    if (isPreferred || !labels.has(id)) {
      labels.set(id, row.display_text as string);
    }
  }
  return labels;
}

export interface LoadAuthSessionInput {
  authUser: User;
  sessionToken: string;
  expiresAt: string;
}

export async function loadAuthSession(
  input: LoadAuthSessionInput,
): Promise<AuthSession> {
  const { authUser, sessionToken, expiresAt } = input;

  // 1. csm_user_master row keyed by email (Supabase auth identity).
  const { data: userRow, error: userErr } = await apiDb
    .from('csm_user_master')
    .select('user_id, internal_code, email, display_name, preferred_locale, is_admin, is_active, can_deactivate_users, can_mimic_users, manager_user_id')
    .eq('email', authUser.email ?? '')
    .single<UserMasterRow>();
  if (userErr) throw userErr;
  if (userRow.is_active !== 'Y') {
    throw new Error('CSM.AUTH.USER_INACTIVE');
  }

  const locale = userRow.preferred_locale || DEFAULT_LOCALE;

  // 2. Role assignments effective today.
  const today = new Date().toISOString().slice(0, 10);
  const { data: roleRows, error: roleErr } = await apiDb
    .from('csm_user_role_assignment')
    .select('role_id, csm_role_master:role_id ( internal_code )')
    .eq('user_id', userRow.user_id)
    .lte('effective_from', today)
    .or(`effective_to.is.null,effective_to.gte.${today}`);
  if (roleErr) throw roleErr;

  const roleAssignments = (roleRows ?? []) as unknown as RoleAssignmentRow[];
  const roleIds = roleAssignments.map(r => r.role_id);
  const roleLabels = await loadTranslations('csm_role_master_tl', 'role_id', roleIds, locale);

  const roles: RoleSummary[] = roleAssignments.map(r => ({
    role_id: r.role_id,
    internal_code: r.csm_role_master.internal_code,
    display_text: roleLabels.get(r.role_id) ?? r.csm_role_master.internal_code,
  }));

  // 3. RBAC matrix.
  //    - Regular users: union of can_* across their assigned roles.
  //    - is_admin='Y':   wildcard - all active pages with all flags TRUE.
  //
  // Navigation is Base-Application-only: csm_page_master doubles as a capability
  // catalog and may hold OTHER applications' non-navigable "action" rows. Only
  // pages of the DEFAULT application (is_default='Y'; NULL app = default/legacy)
  // ever enter page_access, so they are the only ones the menu / palette /
  // breadcrumbs can surface. Resolve the default application id once (generic).
  const { data: defAppRows } = await apiDb
    .from('csm_application_master')
    .select('application_id')
    .eq('is_default', 'Y')
    .order('application_id')
    .limit(1);
  const defaultAppId: number | null = (defAppRows?.[0]?.application_id ?? null) as number | null;

  let pageAccess: PageAccess[];
  if (userRow.is_admin === 'Y') {
    // Scope to the navigable (default) application IN SQL, not just client-side.
    // csm_page_master is a shared capability catalog: other apps' non-navigable
    // "action" rows can outnumber the Base pages many-fold, and PostgREST caps a
    // result set (default 1000 rows). An unfiltered fetch can therefore return a
    // full page of catalog rows and truncate away every navigable page (→ empty
    // menu). Filtering here keeps the result small and correct. When no default
    // app is resolved (defaultAppId null) we fail open — no filter, show all.
    let pageQuery = apiDb
      .from('csm_page_master')
      .select('page_id, internal_code, route_path, parent_page_id, sort_order, desktop_only, application_id')
      .eq('is_active', 'Y');
    if (defaultAppId != null) {
      pageQuery = pageQuery.or(`application_id.is.null,application_id.eq.${defaultAppId}`);
    }
    const { data: pageRows, error: pageErr } = await pageQuery.order('sort_order');
    if (pageErr) throw pageErr;
    pageAccess = ((pageRows ?? []) as {
      page_id: number; internal_code: string; route_path: string;
      parent_page_id: number | null; sort_order: number; desktop_only: 'Y' | 'N';
      application_id: number | null;
    }[])
      .filter(p => isNavigableApplication(p.application_id, defaultAppId))
      .map(p => ({
      page_id: p.page_id,
      page_internal_code: p.internal_code,
      route_path: p.route_path,
      parent_page_id: p.parent_page_id,
      sort_order: p.sort_order,
      can_view: true, can_add: true, can_change: true, can_delete: true, can_signoff: true,
      can_personalize_role: true, can_personalize_user: true,
      desktop_only: p.desktop_only === 'Y',
    }));
  } else {
    const { data: rpaRows, error: rpaErr } = await apiDb
      .from('csm_role_page_access')
      .select(`
        page_id, can_view, can_add, can_change, can_delete, can_signoff, can_personalize_role, can_personalize_user,
        csm_page_master:page_id ( internal_code, route_path, parent_page_id, sort_order, desktop_only, application_id )
      `)
      .in('role_id', roleIds.length > 0 ? roleIds : [-1]);
    if (rpaErr) throw rpaErr;

    const pageAccessMap = new Map<number, PageAccess>();
    for (const row of ((rpaRows ?? []) as unknown as PageAccessRow[])) {
      // Base-Application pages only reach navigation (catalog rows are excluded).
      if (!isNavigableApplication(row.csm_page_master.application_id, defaultAppId)) continue;
      const existing = pageAccessMap.get(row.page_id);
      const merged: PageAccess = {
        page_id: row.page_id,
        page_internal_code: row.csm_page_master.internal_code,
        route_path: row.csm_page_master.route_path,
        parent_page_id: row.csm_page_master.parent_page_id,
        sort_order: row.csm_page_master.sort_order,
        can_view:    !!existing?.can_view    || row.can_view,
        can_add:     !!existing?.can_add     || row.can_add,
        can_change:  !!existing?.can_change  || row.can_change,
        can_delete:  !!existing?.can_delete  || row.can_delete,
        can_signoff: !!existing?.can_signoff || row.can_signoff,
        can_personalize_role: !!existing?.can_personalize_role || !!row.can_personalize_role,
        can_personalize_user: !!existing?.can_personalize_user || !!row.can_personalize_user,
        desktop_only: (existing?.desktop_only ?? false) || row.csm_page_master.desktop_only === 'Y',
      };
      pageAccessMap.set(row.page_id, merged);
    }
    pageAccess = Array.from(pageAccessMap.values())
      .sort((a, b) => a.sort_order - b.sort_order);
  }

  // 4. Favorites.
  const { data: favRows, error: favErr } = await apiDb
    .from('csm_user_favorite_page')
    .select('page_id')
    .eq('user_id', userRow.user_id);
  if (favErr) throw favErr;

  const userContext: UserContext = {
    user_id: userRow.user_id,
    internal_code: userRow.internal_code,
    display_name: userRow.display_name,
    email: userRow.email,
    preferred_locale: locale,
    is_admin: userRow.is_admin === 'Y',
    can_deactivate_users: userRow.can_deactivate_users === 'Y',
    can_mimic_users:      userRow.can_mimic_users === 'Y',
    manager_user_id:      userRow.manager_user_id ?? null,
    roles,
    page_access: pageAccess,
    favorite_page_ids: ((favRows ?? []) as { page_id: number }[]).map((r) => r.page_id),
  };

  return {
    session_token: sessionToken,
    issued_at: new Date().toISOString(),
    expires_at: expiresAt,
    real_admin_id: userRow.user_id, // anchor; may differ from effective during mimic
    effective_user: userContext,
    mimic: {
      is_active: false,
      user_mimic_log_id: null,
      real_admin_id: null,
      mimicked_user_id: null,
      started_at: null,
      business_reason: null,
    },
  };
}

/**
 * Loads page-label translations for the menu. Returns a map keyed by
 * csm_page_master.internal_code (so the UI never depends on numeric ids).
 */
export async function loadPageLabels(
  pageIds: number[],
  locale: LocaleCode,
): Promise<Record<string, string>> {
  if (pageIds.length === 0) return {};

  const { data, error } = await apiDb
    .from('csm_page_master_tl')
    .select('page_id, locale_code, display_text, csm_page_master:page_id ( internal_code )')
    .in('page_id', pageIds)
    .in('locale_code', [locale, DEFAULT_LOCALE]);
  if (error) throw error;

  const labels: Record<string, string> = {};
  const wonByPreferred = new Set<string>();
  for (const row of ((data ?? []) as unknown as {
    locale_code: string;
    display_text: string;
    csm_page_master: { internal_code: string };
  }[])) {
    const code = row.csm_page_master.internal_code;
    const isPreferred = row.locale_code === locale;
    if (isPreferred) {
      labels[code] = row.display_text;
      wonByPreferred.add(code);
    } else if (!wonByPreferred.has(code)) {
      labels[code] = row.display_text;
    }
  }
  return labels;
}
